import { useState, useEffect, useRef, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ListChecks, Loader2 } from 'lucide-react'
import { useResource } from '@/shared/hooks/useResources'
import { useBots } from '@/shared/hooks/useBots'
import { useTelegram } from '@/shared/hooks/useTelegram'
import { useBackButton } from '@/shared/hooks/useBackButton'
import { resourcesApi } from '@/shared/api/resources'
import { botsApi } from '@/shared/api/bots'
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query'
import type { Bot } from '@/shared/types'

export default function ResourceWhitelistAdd() {
  const { resourceId } = useParams<{ resourceId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isReady, hapticFeedback, hapticNotification, showMainButton, hideMainButton, setMainButtonLoading } = useTelegram()
  const { data: resource, isLoading: resourceLoading } = useResource(resourceId)
  const { data: userBots, isLoading: botsLoading } = useBots(resource?.owner_id)

  // Fetch accessible bots (granted access)
  const { data: accessibleBots, isLoading: accessibleBotsLoading, error: accessibleBotsError } = useQuery({
    queryKey: ['accessible-bots'],
    queryFn: async () => {
      console.log('[ResourceWhitelistAdd] Fetching accessible bots...')
      const bots = await botsApi.listBotsWithAccess()
      console.log('[ResourceWhitelistAdd] Accessible bots:', bots)
      return bots
    },
  })

  // Log any errors
  useEffect(() => {
    if (accessibleBotsError) {
      console.error('[ResourceWhitelistAdd] Error fetching accessible bots:', accessibleBotsError)
    }
  }, [accessibleBotsError])

  // Combine owned and accessible bots, avoiding duplicates
  const allBots = useMemo(() => {
    const owned = userBots || []
    const accessible = accessibleBots || []
    console.log('[ResourceWhitelistAdd] Combining bots - owned:', owned.length, 'accessible:', accessible.length)
    const ownedIds = new Set(owned.map(b => b.id))
    const combined = [...owned]
    for (const bot of accessible) {
      if (!ownedIds.has(bot.id)) {
        combined.push(bot)
      }
    }
    console.log('[ResourceWhitelistAdd] Total allBots:', combined.length)
    return combined
  }, [userBots, accessibleBots])

  // Fetch whitelisted bot IDs separately
  const { data: whitelistedBotIds, isLoading: whitelistedIdsLoading } = useQuery({
    queryKey: ['resource-whitelisted-bots', resourceId],
    queryFn: () => resourcesApi.getWhitelistedBots(resourceId!),
    enabled: !!resourceId,
  })

  const [selectedBots, setSelectedBots] = useState<string[]>([])
  const [error, setError] = useState<string>('')

  const handleSaveRef = useRef<() => Promise<void>>()

  // Initialize selected bots from whitelisted IDs
  useEffect(() => {
    if (whitelistedBotIds) {
      setSelectedBots(whitelistedBotIds)
    }
  }, [whitelistedBotIds])

  const toggleBot = (botId: string) => {
    hapticFeedback('light')
    setSelectedBots((prev) =>
      prev.includes(botId)
        ? prev.filter((id) => id !== botId)
        : [...prev, botId]
    )
  }

  const selectAll = () => {
    hapticFeedback('medium')
    setSelectedBots(allBots.map((b) => b.id))
  }

  const clearAll = () => {
    hapticFeedback('medium')
    setSelectedBots([])
  }

  const selectedCount = selectedBots.length

  // Mutation for saving whitelist
  const saveWhitelistMutation = useMutation({
    mutationFn: async (botIds: string[]) => {
      if (!resourceId) throw new Error('Resource ID is required')
      await resourcesApi.setWhitelistedBots(resourceId, botIds)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resource-whitelisted-bots', resourceId] })
      queryClient.invalidateQueries({ queryKey: ['resource', resourceId] })
      queryClient.invalidateQueries({ queryKey: ['resources'] })
      // Invalidate bot-side queries for bidirectional sync
      queryClient.invalidateQueries({ queryKey: ['bot-whitelisted-resources'] })
      queryClient.invalidateQueries({ queryKey: ['bots'] })
    },
  })

  const handleSave = async () => {
    if (!resource) return

    setError('')
    hapticFeedback('medium')
    setMainButtonLoading(true)

    try {
      await saveWhitelistMutation.mutateAsync(selectedBots)
      hapticNotification('success')

      setMainButtonLoading(false)
      hideMainButton()
      window.scrollTo(0, 0)
      navigate(`/traffic/${resourceId}/whitelist`, {
        replace: true,
        state: { successMessage: 'Боты успешно добавлены в белый список' }
      })
    } catch (error) {
      console.error('Failed to save whitelist:', error)
      setError('Ошибка при сохранении ботов')
      hapticNotification('error')
      setMainButtonLoading(false)
    }
  }

  handleSaveRef.current = handleSave

  // Auto-manage back button
  useBackButton()

  // Setup MainButton
  useEffect(() => {
    if (!isReady) return

    window.scrollTo(0, 0)

    const onSave = () => {
      handleSaveRef.current?.()
    }

    setMainButtonLoading(false)
    showMainButton('Добавить выбранные', onSave, '#5288c1', '#ffffff')

    document.body.style.backgroundColor = '#1e2832'

    return () => {
      hideMainButton()
      document.body.style.backgroundColor = ''
    }
  }, [isReady, showMainButton, hideMainButton, setMainButtonLoading])

  // Helper to get logo URL
  const getLogoUrl = (bot: Bot) => {
    if (!bot.id) return null

    const baseUrl = import.meta.env.VITE_OPSERVICE_BASE_URL || 'http://localhost:8080'
    return `${baseUrl}/api/v1/bots/${bot.id}/logo`
  }

  if (resourceLoading || botsLoading || accessibleBotsLoading || whitelistedIdsLoading || !resource) {
    return (
      <div className="min-h-screen bg-tg-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-tg-bg pb-24 pt-[96px]">
      {/* Header */}
      <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center">
              <ListChecks className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-tg-text">Добавить ботов</h1>
              <p className="text-sm text-tg-hint mt-1">{resource.name}</p>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-tg-text">{selectedCount}</div>
              <div className="text-xs text-tg-hint">выбрано</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {/* Info Card */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
          <p className="text-sm text-blue-400">
            Выберите свои боты для продвижения этого ресурса. Оплата не требуется.
          </p>
        </div>

        {allBots.length === 0 ? (
          <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden p-8 text-center">
            <p className="text-tg-hint">У вас пока нет ботов</p>
            <button
              onClick={() => {
                hapticFeedback('light')
                navigate('/bots')
              }}
              className="mt-4 px-6 py-2 bg-tg-button text-white rounded-xl"
            >
              Создать бота
            </button>
          </div>
        ) : (
          <>
            {/* Batch Actions */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  selectAll()
                }}
                disabled={allBots.length === 0}
                className="flex-1 py-2 px-3 bg-[#5288c1]/30 text-[#5288c1] rounded-lg text-sm font-medium hover:bg-[#5288c1]/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Выбрать все ({allBots.length})
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  clearAll()
                }}
                disabled={selectedCount === 0}
                className="flex-1 py-2 px-3 bg-red-500/20 text-red-600 rounded-lg text-sm font-medium hover:bg-red-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Очистить ({selectedCount})
              </button>
            </div>

            {/* Bots List */}
            <div className="space-y-3">
              {allBots.map((bot) => {
                const isSelected = selectedBots.includes(bot.id)
                const logoUrl = getLogoUrl(bot)

                return (
                  <button
                    key={bot.id}
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      toggleBot(bot.id)
                    }}
                    className={`w-full relative bg-tg-secondary-bg rounded-2xl p-4 shadow-sm transition-all ${
                      isSelected
                        ? 'ring-2 ring-tg-button bg-tg-button/5'
                        : 'hover:shadow-md active:scale-[0.98]'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Bot Logo or Icon */}
                      <div className={`relative flex-shrink-0 w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center ${
                        logoUrl ? 'bg-tg-bg' : 'bg-tg-hint/10'
                      }`}>
                        {logoUrl ? (
                          <img
                            src={logoUrl}
                            alt={bot.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.style.display = 'none'
                              const parent = target.parentElement
                              if (parent) {
                                parent.innerHTML = `<div class="text-2xl">🤖</div>`
                              }
                            }}
                          />
                        ) : (
                          <div className="text-2xl">🤖</div>
                        )}
                      </div>

                      {/* Bot Info */}
                      <div className="flex-1 min-w-0 text-left">
                        <h3 className="text-base font-semibold text-tg-text truncate mb-0.5">
                          {bot.name}
                        </h3>
                        <p className="text-sm text-tg-hint truncate">
                          @{bot.username}
                        </p>
                      </div>

                      {/* Selection Indicator */}
                      <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                        isSelected
                          ? 'bg-tg-button border-tg-button'
                          : 'border-tg-hint/30'
                      }`}>
                        {isSelected && (
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
